import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/Button';
import { PayrollProviderName } from 'api/EmployersApi';
import { getApplicationData } from 'selectors/getApplicationData';
import FormNavigation, { FormNavigationProps } from 'components/FormNavigation';
import { FlowComponentType } from 'routes/types';
import { ReactComponent as FastBill } from 'images/fast-bill-w-clouds.svg';
import { getEmployerData, updatePayrollProviderToManual } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { RootState } from 'handlers';

import { getPayrollWidget } from './payrollProvider/PayrollProvider';

import styles from './AutomaticDeduction.module.scss';

const OUTPUT_VARIABLES_DECIMAL_PLACES = 2;

interface AutomaticDeductionProps extends FlowComponentType {
  NavigationComponent?: FC<FormNavigationProps>;
}

const AutomaticDeduction = ({
  navigationInfo,
  handleNext,
  NavigationComponent = FormNavigation,
}: AutomaticDeductionProps) => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { application, isApplicationStatusUpdating, isLoading: isApplicationLoading } = useSelector(getApplicationData);
  const payrollProvider = application?.employment.employer1?.payrollProvider || PayrollProviderName.Argyle;
  const { employerData, isLoading: isFetchingEmployerData } = useSelector(
    (state: RootState) => state.employers,
  ).getEmployerData;
  const employer1 = application?.employment.employer1;
  const employerId = employer1?.linkItemId;

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!employerData && payrollProvider && employerId) {
      dispatch(getEmployerData({ payrollProvider, employerId }));
    }
  }, [employerData, payrollProvider, employerId]);

  const handleOpenPayrollProvider = async () => {
    setOpen(true);
  };

  const handleUpdatePayrollProviderToManual = async () => {
    await dispatchWithUnwrap(updatePayrollProviderToManual(application!.id));
  };

  const PayrollWidget = payrollProvider && getPayrollWidget(payrollProvider);
  return (
    <>
      <NavigationComponent {...navigationInfo} />
      <div className={styles.container}>
        <h1 className={styles.title}>Next, let’s update your direct deposit</h1>
        <p className={styles.description}>
          We are partners with {payrollProvider}, which provides an easy and secure way to{' '}
          {application?.fullDepositSwitch ? 'update your direct deposit.' : 'set up payroll deductions.'}
        </p>
        {!application?.fullDepositSwitch && application?.amountPerPaycheck && (
          <div className={styles.outputValueContainer}>
            <div className={styles.outputValueItem}>
              <p className={styles.subtitle}>Amount per paycheck</p>
              <p className={styles.outputValue}>
                ${application?.amountPerPaycheck.toFixed(OUTPUT_VARIABLES_DECIMAL_PLACES)}
              </p>
            </div>
          </div>
        )}
        <FastBill />
        {application?.fullDepositSwitch && (
          <p className={styles.notification}>
            On payday, we will automatically deduct your loan payment and send the rest to your external checking
            account.
          </p>
        )}
        <Button
          className={styles.button}
          onClick={handleOpenPayrollProvider}
          isLoading={isApplicationStatusUpdating || isFetchingEmployerData || isApplicationLoading}
        >
          Get Started
        </Button>
        <>
          {PayrollWidget && application && (
            <PayrollWidget
              open={open}
              onClose={() => setOpen(false)}
              onSuccess={handleNext}
              application={application!}
              onUpdatePayrollProviderToManual={handleUpdatePayrollProviderToManual}
              isDDS
            />
          )}
        </>
      </div>
    </>
  );
};

export default AutomaticDeduction;
